<template>
  <VDataGridV2
      id="transport-orders-registry"
      :loader="dataLoader"
      paginated
      :skeleton-row-count="16"
      entity-type="transportOrder"
      :context-menu="TransportOrdersService.contextMenu"
      @row:dblclick="openCard"
  >
    <!-- № -->
    <VDataGridColumnV2
        v-if="isColumnVisible('id')"
        id="id"
        width="80px"
        sortable
        content-class="center"
        sort-default-value="desc"
        :filter="$core.filters.idPattern"
        content-align="left"
        label="№"
    >
      <template #default="{ row }">
        <VLink
            @click="openCard(row)"
        >
          {{ $core.utils.string.formatWithDelimiter(row.id) }}
        </VLink>
        <div class="description">
          <VBadge v-if="row.isNew === true" class="primary">Новая</VBadge>
        </div>
      </template>
    </VDataGridColumnV2>

    <!-- Тип ЗТК -->
    <VDataGridColumnV2
      v-if="isColumnVisible('type')"
      id="type"
      width="120px"
      label="Тип"
      content-class="ellipsis"
      filterable
      filter-type="select"
      :filter-props="{
          options: TransportOrder.types,
           clearable: true
        }"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.typeString"
        >
          {{ row.typeString }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('status')"
      id="status"
      width="120px"
      label="Статус"
      filterable
      filter-type="select"
      :filter-props="{ options: TransportOrdersService.TABS, clearable: true }"
      filter-identity="isInWork,isConfirmed,isFinished"
    >
      <template #default="{ row }">
        {{ row.status }}
        <VBadges>
          <VBadge v-if="row.hasDowntime" class="warning">Есть простои</VBadge>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <!-- Грузоотправитель -->
    <VDataGridColumnV2
        id="sender"
        v-if="isColumnVisible('sender')"
        sortable
        width="100px"
        filterable
        filter-identity="*"
        filter-type="select"
        :filter-props="{
				searchFunction: (pattern) => PartnersService.searchSellers(pattern),
				placeholder: 'Название партнера',
				clearable: true,
			}"
        filter-by-value
    >
      <template #label>
        <div>Грузоотправитель</div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis" :title="row.sender?.name">
          {{ row.sender?.name || '--' }}
        </div>
        <div
          class="description ellipsis"
          :title="row.senderStore?.name || row.senderStore?.address || '--' "
        >
          {{ row.senderStore?.name || row.senderStore?.address || '--'}}
        </div>
      </template>
    </VDataGridColumnV2>

    <!-- Грузополучатель -->
    <VDataGridColumnV2
        id="receiver"
        v-if="isColumnVisible('receiver')"
        sortable
        width="100px"
        label="Грузополучатель"
        filterable
        filter-identity="*"
        filter-type="select"
        :filter-props="{
				searchFunction: (pattern) => PartnersService.searchBuyers(pattern),
				placeholder: 'Название партнера',
				clearable: true,
			}"
    >
      <template #default="{ row }">
        <div class="ellipsis" :title="row.receiver?.name">
          {{ row.receiver?.name || '--' }}
        </div>
        <div
          class="description ellipsis"
          :title="row.receiverStore?.name || row.receiverStore?.address || '--'"
        >
          {{ row.receiverStore?.name || row.receiverStore?.address || '--' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <!-- Дата забора плановая -->
    <VDataGridColumnV2
        v-if="isColumnVisible('pickupDatePlanned')"
        id="pickupDatePlanned"
        width="135px"
        sortable
        content-align="center"
        :filter="$core.filters.period('pickupDatePlannedPeriod')"
    >
      <template #label>
        <div>Забор план</div>
        <div class="description">Факт</div>
      </template>

      <template #default="{ row }">
        {{ $core.utils.date.format(row.pickupDatePlanned) || '--' }}
        <div class="description">
          {{ row.pickupDateActual ? $core.utils.date.format(row.pickupDateActual) : '--' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <!-- Дата получения плановая -->
    <VDataGridColumnV2
        v-if="isColumnVisible('arrivalDatePlanned')"
        id="arrivalDatePlanned"
        width="155px"
        sortable
        content-align="center"
        :filter="$core.filters.period('arrivalDatePlannedPeriod')"
    >
      <template #label>
        <div>Получение план</div>
        <div class="description">Факт</div>
      </template>

      <template #default="{ row }">
        {{ row.arrivalDatePlanned ? $core.utils.date.format(row.arrivalDatePlanned) : '--' }}
        <div class="description">
          {{ row.arrivalDateActual ? $core.utils.date.format(row.arrivalDateActual) : '--' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('storeTemperatureRange')"
        id="storeTemperatureRange"
        label="Темп режим, °C"
        width="140px"
    >
      <template #default="{ row }">
        {{ row.storeTemperatureRange.toString({ unit: '°C' }) || '--' }}
      </template>
    </VDataGridColumnV2>

    <!-- Перевозчик + Трек номер -->
    <VDataGridColumnV2
        v-if="isColumnVisible('delivererName')"
        id="delivererName"
        width="130px"
    >
      <template #label>
        <div>Перевозчик</div>
        <div class="description">Трек номер</div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis" :title="row.deliverer?.name">
          {{ row.deliverer?.name || '--' }}
        </div>
        <div class="description">
          {{ row.trackingNumber || 'Не задано' }}
        </div>
      </template>
    </VDataGridColumnV2>


    <!-- Загрузка транспорта: погружено / сколько ещё влезет; транспорт не выбран — «--» -->
    <VDataGridColumnV2
        v-if="isColumnVisible('cargoWeight')"
        id="cargoWeight"
        label="Вес брутто, кг"
        width="120px"
    >
      <template #default="{ row }">
        <VehicleLoadBar :load="row.cargoWeightGross" :capacity="row.vehicle?.maxWeight ?? null"/>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('cargoUnits')"
        id="cargoUnits"
        label="Места"
        width="100px"
    >
      <template #default="{ row }">
        <VehicleLoadBar :load="row.cargoUnitsCount" :capacity="row.vehicle?.cargoPlacesCount ?? null"/>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('cargoVolume')"
        id="cargoVolume"
        label="Объём, м³"
        width="100px"
    >
      <template #default="{ row }">
        <VehicleLoadBar :load="row.cargoVolume" :capacity="row.vehicle?.maxVolume ?? null"/>
      </template>
    </VDataGridColumnV2>

    <!-- Дедлайн текущего этапа -->
    <VDataGridColumnV2
        v-if="isColumnVisible('deadline')"
        id="deadline"
        label="Дедлайн"
        width="110px"
        content-align="left"
        sortable
        @edit="showStatusPopup"
    >
      <template #default="{ row }">
        {{ $core.utils.date.format(row.deadline) || '--' }}
      </template>
    </VDataGridColumnV2>

    <!-- Таможенный статус -->
    <VDataGridColumnV2
        v-if="isColumnVisible('borderStatus')"
        id="borderStatus"
        label="Таможня"
        width="140px"
        content-class="ellipsis"
    >
      <template #default="{ row }">
        <div class="ellipsis" :title="row.borderStatus?.name">
          {{ row.borderStatus?.name || '--' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <!-- Комментарий -->
    <VDataGridColumnV2
        v-if="isColumnVisible('comment')"
        id="comment"
        label="Комментарий"
        width="200px"
        content-class="ellipsis"
    >
      <template #default="{ row }">
        <LastComment
          :entity="row"
          entity-type-name="transportOrder"
        />
      </template>
    </VDataGridColumnV2>

    <!-- Кнопки создания ЗТК -->
    <VDataGridActionBarV2>
      <TransportOrderCreateButtons
          v-if="createButtons.length > 0"
          :types="createButtons"
          @transport-order:created="onTransportOrderCreated"
      />
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<style lang="scss">
#transport-orders-registry {

  .datagrid {

    td {
      &[data-column-id="sender"] ,
      &[data-column-id="receiver"] {
        max-width: 100px;
      }
    }
  }
}

</style>

<script lang="ts">

import DataGridLoader from '@/lib/components/VDataGridV2/DataGridLoader';
import PartnersService from '@/domains/Partners/PartnersService';
import TransportOrdersService from '@/domains/TransportOrders/TransportOrdersService';
import TransportOrder from "@/domains/TransportOrders/TransportOrder";
import VehicleLoadBar from '@/domains/TransportOrders/components/VehicleLoadBar/VehicleLoadBar.vue';

import TransportOrderCreateButtons
  from '@/domains/TransportOrders/components/TransportOrderCreateButtons.vue';
import type { TransportOrderCreateButtonType }
  from '@/domains/TransportOrders/components/TransportOrderCreateButtons.vue';

export default defineComponent({
  name: 'TransportOrdersRegistry',

  components: {
    TransportOrderCreateButtons,
    VehicleLoadBar,
  },

  props: {
    params: {
      type: Object as PropType<Parameters<typeof TransportOrdersService.getListPaginated>[0]>,
      default: () => ({}),
    },

    /**
     * Типы ЗТК, которые можно создать с этого экрана. Пустой список —
     * экшн-бар не рендерится.
     */
    createButtons: {
      type: Array as PropType<TransportOrderCreateButtonType[]>,
      default: () => []
    },

    hiddenColumns: {
      type: Array as PropType<string[]>,
      default: () => []
    }
  },

  setup() {
    return {
      TransportOrdersService,
      PartnersService,
      TransportOrder
    }
  },

  data() {
    return {
      dataLoader: null as DataGridLoader,
    };
  },

  created() {
    this.dataLoader = new DataGridLoader(TransportOrdersService.getListPaginated)
      .useExtraParams({ requireCommentsInfo: true })
      .useReactiveSource(() => this.params);
  },

  computed: {
    hiddenColumnsSet(): Set<string> {
      return new Set(this.hiddenColumns);
    },
  },

  methods: {
    async openCard(row: TransportOrder): Promise<void> {
      if (await TransportOrdersService.showTransportOrderDialog(row.id) ?? null) {
        await this.dataLoader.refresh();
      }
    },

    /** Открывает попап смены статуса у ячейки и обновляет реестр, только если ЗТК сохранили */
    async showStatusPopup(row: TransportOrder, anchor: HTMLElement): Promise<void> {
      const isSaved = await TransportOrdersService.showStatusPopup(row, anchor);

      if (isSaved === true) {
        this.dataLoader.refresh();
      }
      else {
        // закрыли без сохранения — данные не менялись
      }
    },

    refresh(): void {
      this.dataLoader.refresh();
    },

    onTransportOrderCreated(): void {
      this.dataLoader.refresh();
    },

    isColumnVisible(columnId: string): boolean {
      return this.hiddenColumnsSet.has(columnId) === false;
    },
  },
});
</script>
